<template>
  <div class="home">
    <div class="banner-section">
      <div class="container">
        <SidebarMenu />
        <div class="carousel-wrapper">
          <Carousel />
        </div>
      </div>
    </div>
    
    <!-- Fixed Floating Coupon Center -->
    <div class="coupon-floating-widget" :class="{ expanded: couponPanelVisible }">
      <!-- Floating Trigger Button -->
      <div class="trigger-btn" @click="couponPanelVisible = !couponPanelVisible" :title="couponPanelVisible ? '关闭领券' : '领券中心'">
        <div class="icon-wrap">
          <i class="el-icon-present"></i>
          <span class="count-badge">{{ coupons.length }}</span>
        </div>
        <div class="text">领券</div>
      </div>

      <!-- Slide-out Panel -->
      <div class="coupon-panel">
        <div class="panel-header-tabs">
          <div 
            class="tab-item" 
            :class="{ active: activeCouponTab === 'available' }"
            @click="activeCouponTab = 'available'"
          >领券中心</div>
          <div 
            class="tab-item" 
            :class="{ active: activeCouponTab === 'my' }"
            @click="switchMyTab"
          >我的优惠券</div>
          <i class="el-icon-close close-btn" @click="couponPanelVisible = false"></i>
        </div>
        
        <div class="panel-body">
          <!-- Available List -->
          <div v-if="activeCouponTab === 'available'">
            <div class="panel-coupon-card" v-for="coupon in coupons" :key="'avail-' + coupon.id">
              <div class="card-main">
                <div class="amt-box">
                  <span class="u">¥</span>
                  <span class="v">{{ coupon.value }}</span>
                </div>
                <div class="info-box">
                  <div class="name">{{ coupon.name }}</div>
                  <div class="cond">满{{ coupon.minAmount }}可用</div>
                </div>
              </div>
              <div class="card-footer">
                <div class="valid">有效期 {{ coupon.validDays }} 天</div>
                <div class="btn" @click="handleReceiveCoupon(coupon)">立即领取</div>
              </div>
              <div class="edge-dot doc-top"></div>
              <div class="edge-dot doc-bottom"></div>
            </div>
            <!-- Empty state for available list -->
            <div v-if="coupons.length === 0" class="panel-empty">
              <i class="el-icon-shopping-bag-2"></i>
              <p>暂无可用优惠券</p>
            </div>
          </div>
          
          <!-- My Coupons List -->
          <div v-else>
            <div class="panel-coupon-card my-card" v-for="coupon in myCoupons" :key="'my-' + coupon.id" :class="'status-' + coupon.status">
              <div class="card-main">
                <div class="amt-box">
                  <span class="u">¥</span>
                  <span class="v">{{ coupon.value }}</span>
                </div>
                <div class="info-box">
                  <div class="name">{{ coupon.name }}</div>
                  <div class="cond">满{{ coupon.minAmount }}可用</div>
                </div>
              </div>
              <div class="card-footer">
                <div class="valid">{{ coupon.validEndTime }} 到期</div>
                <div class="status-btn" v-if="coupon.status === 0" @click="goToProducts">立即去使用</div>
                <div class="status-text" v-else-if="coupon.status === 1">已使用</div>
                <div class="status-text" v-else>已失效</div>
              </div>
              <div class="edge-dot doc-top"></div>
              <div class="edge-dot doc-bottom"></div>
            </div>
            <!-- Empty state for my list -->
            <div v-if="myCoupons.length === 0" class="panel-empty">
              <i class="el-icon-tickets"></i>
              <p>您还没有领取过优惠券</p>
            </div>
          </div>
        </div>
        
        <div class="panel-footer">
          {{ activeCouponTab === 'available' ? '下单立减 · 限时抢券' : '好券在手 · 下单更省' }}
        </div>
      </div>
    </div>
    
    <!-- Selected Categories Module -->
    <div class="selected-categories">
      <div class="container">
        <h2 class="module-title">精选品类</h2>
        <div class="category-cards">
          <div 
            class="cat-card" 
            v-for="(item, index) in selectedCategories" 
            :key="index"
            @click="goToCategoryDetail(item)"
          >
            <div class="card-info">
              <h3 class="card-title">{{ item.title }}</h3>
              <div class="sub-tags">
                <span v-for="(sub, sIdx) in item.subCategories.slice(0, 3)" :key="sIdx">{{ sub }}</span>
              </div>
              <div class="go-detail">立即进入 <i class="el-icon-right"></i></div>
            </div>
            <div class="card-image">
              <img :src="item.bgImage" :alt="item.title">
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 代找商品模块（已移至 Header） -->
    
    <!-- 动态楼层模块 -->
    <div class="main-content">
      <div class="container">
        <div v-for="(section, sIndex) in homeSections" :key="section.sectionId" class="dynamic-floor">
          <div class="floor-header">
            <h3>{{ section.title }}</h3>
            <div class="more-link">
              <router-link :to="'/list?categoryId=' + section.categoryId">更多 &gt;</router-link>
            </div>
          </div>
          <div class="floor-content">
            <div class="floor-left-banner" :style="section.imageUrl ? { backgroundImage: `linear-gradient(to bottom, rgba(20, 35, 55, 0.8) 0%, rgba(30, 45, 65, 0.95) 100%), url(${section.imageUrl})` } : {}">
              <div class="floor-tag-wrapper">
                <div class="floor-number">0{{ sIndex + 1 }}</div>
                <div class="floor-divider"></div>
              </div>

              <div class="sub-category-tags">
                <span 
                  v-for="sub in section.subCategories" 
                  :key="sub.id"
                  :class="{ active: section.activeSubId === sub.id }"
                  @click="switchSubCategory(section, sub.id)"
                >{{ sub.name }}</span>
              </div>
            </div>
            <div class="floor-products">
              <div 
                class="product-card" 
                v-for="product in section.products.slice(0, 12)" 
                :key="product.id"
                @click="goToDetail(product, section.categoryId)"
              >
                <div class="p-img">
                  <img :src="product.mainImage" :alt="product.name">
                </div>
                <div class="p-info">
                  <div class="p-name" :title="product.name">{{ product.name }}</div>
                  <div class="p-price-row">
                    <span class="p-price">¥{{ product.price }}</span>
                    <span class="cart-btn" @click.stop="addToCart(product)" title="加入购物车">
                      <i class="el-icon-shopping-cart-2"></i>
                    </span>
                  </div>
                </div>
              </div>
              <!-- 空状态 -->
              <div v-if="!section.products || section.products.length === 0" class="empty-products">
                <i class="el-icon-shopping-bag-2"></i>
                <p>暂无商品</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- SKU 选择弹窗 -->
    <el-dialog title="选择规格" :visible.sync="skuDialogVisible" width="500px" class="sku-dialog">
      <div v-if="currentProductForSku" class="sku-dialog-content">
        <div class="product-brief">
          <img :src="currentSkuImage || currentProductForSku.mainImage" alt="product">
          <div class="brief-info">
            <div class="name">{{ currentProductForSku.name }}</div>
            <div class="price">
              <span class="currency">¥</span>
              <span class="value">{{ currentSkuPrice || currentProductForSku.price }}</span>
            </div>
            <div class="selected-text" v-if="selectedSku">
              已选：{{ formattedSelectedSpecs }}
            </div>
            <div class="moq-tip" v-if="currentProductForSku.minOrderQuantity > 1">
              起订量：{{ currentProductForSku.minOrderQuantity }}{{ currentProductForSku.unit || '个' }}
            </div>
          </div>
        </div>

        <div class="spec-list-wrap">
          <div v-for="(spec, sIndex) in specList" :key="sIndex" class="spec-section">
            <div class="spec-title">{{ spec.name }}</div>
            <div class="sku-options">
              <div 
                class="sku-option-item" 
                v-for="(val, vIndex) in spec.values" 
                :key="vIndex"
                :class="{ active: selectedSpecs[spec.name] === val }"
                @click="selectSpec(spec.name, val)"
              >
                {{ val }}
              </div>
            </div>
          </div>
        </div>

        <div class="quantity-row">
          <span class="label">购买数量</span>
          <el-input-number v-model="skuBuyQuantity" :min="currentProductForSku ? currentProductForSku.minOrderQuantity || 1 : 1" :max="9999" size="small"></el-input-number>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="skuDialogVisible = false">取 消</el-button>
        <el-button type="warning" style="background-color: #d97706;" @click="confirmSkuAddToCart" :disabled="!isSkuSelected">确认加入购物车</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import SidebarMenu from '@/components/SidebarMenu.vue'
import Carousel from '@/components/Carousel.vue'
import { getHomeSections, getHomeSectionsWithProducts, getProductList, getProductDetail } from '@/api/product'
import { addToCart as addToCartApi } from '@/api/cart'
import { getAvailableCoupons, receiveCoupon, getMyCoupons } from '@/api/coupon'
import { getToken } from '@/utils/auth'
import { mapActions } from 'vuex'

export default {
  name: 'Home',
  components: {
    SidebarMenu,
    Carousel
  },
  data() {
    return {
      homeSections: [], // 动态楼层数据
      // SKU 选择相关
      skuDialogVisible: false,
      currentProductForSku: null,
      specList: [],
      skuList: [],
      selectedSpecs: {},
      selectedSku: null,
      skuBuyQuantity: 1,
      selectedCategories: [],
      coupons: [], // 优惠券列表
      myCoupons: [], // 我的优惠券列表
      activeCouponTab: 'available', // available, my
      couponPanelVisible: false // 悬浮框展开状态
    }
  },
  computed: {
    formattedSelectedSpecs() {
      return Object.values(this.selectedSpecs).join(' / ')
    },
    isSkuSelected() {
      if (this.specList.length === 0) return true
      return this.specList.every(spec => !!this.selectedSpecs[spec.name]) && !!this.selectedSku
    },
    currentSkuPrice() {
      return this.selectedSku ? this.selectedSku.price : null
    },
    currentSkuImage() {
      return this.selectedSku ? this.selectedSku.image : null
    }
  },
  mounted() {
    this.fetchSelectedCategories()
    this.fetchHomeSections()
    if (getToken()) {
      this.fetchCoupons()
    }
  },
  methods: {
    // 获取精选品类数据
    async fetchSelectedCategories() {
      try {
        const res = await getHomeSections()
        if (res.code === 200 && res.data) {
          // 只取前5个作为精选展示
          this.selectedCategories = res.data.slice(0, 5).map(item => ({
            sectionId: item.sectionId,
            title: item.title,
            bgImage: item.imageUrl,
            subCategories: (item.categories || []).map(cat => cat.categoryName)
          }))
        }
      } catch (error) {
        console.error('获取精选品类失败:', error)
      }
    },
    // 跳转精选品类详情
    goToCategoryDetail(item) {
      this.$router.push({
        path: '/category/detail',
        query: { sectionId: item.sectionId }
      })
    },
    // 获取首页楼层数据
    async fetchHomeSections() {
      try {
        const res = await getHomeSectionsWithProducts()
        if (res.code === 200 && res.data) {
          this.homeSections = res.data.map(section => {
             // 提取所有子分类及其关联的商品
             const subCategories = (section.categories || []).map(cat => ({
               id: cat.categoryId,
               name: cat.categoryName,
               products: cat.products || []
             }));
             
             // 默认选中第一个子分类
             const defaultSub = subCategories.length > 0 ? subCategories[0] : null;
             
             return {
               sectionId: section.sectionId,
               title: section.title,
               imageUrl: section.imageUrl,
               activeSubId: defaultSub ? defaultSub.id : null,
               categoryId: defaultSub ? defaultSub.id : null, // 兼容 More 链接
               subCategories: subCategories,
               products: defaultSub ? defaultSub.products : []
             }
          })
        }
      } catch (error) {
        console.error('获取首页楼层数据失败:', error)
      }
    },
    // 切换子分类
    switchSubCategory(section, subId) {
      section.activeSubId = subId
      // 从已加载的子分类数据中查找对应的商品列表
      const sub = section.subCategories.find(s => s.id === subId)
      if (sub) {
        section.products = sub.products || []
        section.categoryId = sub.id // 同步更新更多链接的 ID
      }
    },
    // 跳转商品详情
    goToDetail(product, categoryId) {
      this.$router.push({
        path: `/product/${product.id}`,
        query: { categoryId }
      })
    },
    // 搜索
    handleSearch(keyword) {
      this.$router.push({ path: '/list', query: { keyword } })
    },
    // 选择规格
    selectSpec(name, val) {
      this.$set(this.selectedSpecs, name, val)
      this.updateSelectedSku()
    },
    // 更新匹配的SKU
    updateSelectedSku() {
      const match = this.skuList.find(sku => {
        try {
          const skuSpecs = JSON.parse(sku.specData || '{}')
          return Object.keys(this.selectedSpecs).every(key => skuSpecs[key] === this.selectedSpecs[key])
        } catch (e) {
          return false
        }
      })
      this.selectedSku = match || null
    },
    // 加入购物车
    async addToCart(product) {
      // 检查登录
      if (!getToken()) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }

      try {
        const res = await getProductDetail(product.id)
        if (res.code === 200 && res.data) {
          const data = res.data
          const productSkus = data.productSkus || []
          const productSpecs = data.productSpecs || []
          // 确保 currentProductForSku 包含 minOrderQuantity
          product.minOrderQuantity = data.minOrderQuantity || 1
          product.unit = data.unit || '个'
          
          // 如果有多规格，弹出选择框
          if (productSpecs.length > 0 || productSkus.length > 1) {
            this.currentProductForSku = product
            this.specList = productSpecs.map(spec => ({
              name: spec.specName,
              values: JSON.parse(spec.specValues || '[]')
            }))
            this.skuList = productSkus
            this.selectedSpecs = {}
            this.selectedSku = null
            this.skuBuyQuantity = data.minOrderQuantity || 1
            this.skuDialogVisible = true

            // 默认选中第一个SKU
            if (this.skuList.length > 0) {
              try {
                this.selectedSpecs = JSON.parse(this.skuList[0].specData || '{}')
                this.updateSelectedSku()
              } catch (e) {
                console.error('解析SKU specData失败', e)
              }
            }
          } else {
            // 无多规格，直接添加
            const cartRes = await addToCartApi({
              productId: product.id,
              quantity: data.minOrderQuantity || 1,
              productType: 1,
              productName: product.name,
              productImage: product.mainImage,
              price: product.price,
              selected: 1,
              skuId: productSkus.length > 0 ? productSkus[0].id : 0,
              skuSpec: productSkus.length > 0 ? productSkus[0].specData : '{}'
            })
            if (cartRes.code === 200) {
              this.$message.success('加入购物车成功!')
              this.fetchCartCount()
            }
          }
        }
      } catch (error) {
        console.error('获取详情或加入购物车失败:', error)
      }
    },
    // 确认SKU并加入购物车
    async confirmSkuAddToCart() {
      if (!this.isSkuSelected) {
        this.$message.warning('请选择完整的规格')
        return
      }

      const product = this.currentProductForSku
      try {
        const res = await addToCartApi({
          productId: product.id,
          productType: 1,
          productName: product.name,
          productImage: this.selectedSku ? this.selectedSku.image : product.mainImage,
          skuId: this.selectedSku ? this.selectedSku.id : 0,
          skuSpec: JSON.stringify(this.selectedSpecs),
          price: this.selectedSku ? this.selectedSku.price : product.price,
          quantity: this.skuBuyQuantity,
          selected: 1
        })
        if (res.code === 200) {
          this.$message.success('加入购物车成功!')
          this.skuDialogVisible = false
          this.fetchCartCount()
        }
      } catch (error) {
        console.error('加入购物车失败:', error)
      }
    },
    // 获取可用优惠券
    async fetchCoupons() {
      try {
        const res = await getAvailableCoupons()
        if (res.code === 200) {
          this.coupons = res.rows || []
        }
      } catch (error) {
        console.error('获取优惠券失败:', error)
      }
    },
    // 获取我的优惠券
    async fetchMyCoupons() {
      try {
        const res = await getMyCoupons({ pageNum: 1, pageSize: 20 })
        if (res.code === 200) {
          this.myCoupons = res.rows || []
        }
      } catch (error) {
        console.error('获取我的优惠券失败:', error)
      }
    },
    // 切换到我的优惠券页签
    switchMyTab() {
      this.activeCouponTab = 'my'
      this.fetchMyCoupons()
    },
    // 跳转商品列表去使用
    goToProducts() {
      this.couponPanelVisible = false
      this.$router.push('/list')
    },
    // 领取优惠券
    async handleReceiveCoupon(coupon) {
      try {
        const res = await receiveCoupon(coupon.id)
        if (res.code === 200) {
          this.$message.success('领取成功！可立即在“我的优惠券”中查看')
          // 领取后刷新可用列表
          this.fetchCoupons()
          // 如果已经在“我的”列表预览过，也可能需要刷新
          if (this.myCoupons.length > 0) this.fetchMyCoupons()
        }
      } catch (error) {
        console.error('领取优惠券失败:', error)
      }
    },
    ...mapActions(['fetchCartCount'])
  }
}
</script>

<style lang="scss" scoped>
.container {
  width: 1400px;
  max-width: 95%;
  margin: 0px auto;
}

.banner-section {
  background-color: #f7f9fa;
  
  .container {
    display: flex;
    position: relative;
    padding: 20px 0;
  }

  .carousel-wrapper {
    flex: 1;
    margin-left: 10px;
    height: 480px;
    position: relative;
    z-index: 1;
  }
}

.main-content {
  padding-bottom: 50px;
}
// 响应式适配 - 平板到PC端
@media (min-width:1400px) {
  .container { width: 1400px; }
  .banner-section .carousel-wrapper { height: 480px; }
}

// 响应式适配 - 平板到PC端
@media (max-width: 1399px) and (min-width: 1200px) {
  .container { width: 1180px; }
  .banner-section .carousel-wrapper { height: 420px; }
}

@media (max-width: 1199px) and (min-width: 992px) {
  .container { width: 970px; }
  .banner-section .carousel-wrapper { height: 380px; }
}

@media (max-width: 991px) and (min-width: 768px) {
  .container { width: 750px; }
  .banner-section .carousel-wrapper { height: 320px; }
}

// 悬浮式领券中心
.coupon-floating-widget {
  position: fixed;
  right: 20px;
  bottom: 300px;
  width: 64px;
  height: 64px;
  z-index: 2000;
  pointer-events: none;
  
  // 悬浮球按钮
  .trigger-btn {
    pointer-events: auto;
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #e1251b 0%, #ff4b2b 100%);
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    cursor: pointer;
    box-shadow: 0 10px 30px rgba(225, 37, 27, 0.4);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    z-index: 10;
    
    .icon-wrap {
      position: relative;
      margin-bottom: 2px;
      i { font-size: 24px; animation: wobble 2s infinite; }
      .count-badge {
        position: absolute;
        top: -6px;
        right: -10px;
        background: #fff;
        color: #e1251b;
        font-size: 10px;
        font-weight: bold;
        padding: 1px 5px;
        border-radius: 10px;
        border: 1px solid #e1251b;
      }
    }
    
    .text { font-size: 11px; font-weight: bold; }

    &:hover {
      transform: scale(1.1) rotate(5deg);
      box-shadow: 0 15px 40px rgba(225, 37, 27, 0.5);
    }
  }

  // 侧边面板
  .coupon-panel {
    pointer-events: auto;
    position: absolute;
    right: 80px;
    bottom: 0;
    width: 300px;
    max-height: 500px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(225, 37, 27, 0.1);
    border-radius: 16px;
    box-shadow: 0 15px 50px rgba(0,0,0,0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform: translateX(360px) scale(0.9);
    opacity: 0;
    visibility: hidden;
    transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    transform-origin: right bottom;
    
    .panel-header-tabs {
      display: flex;
      align-items: center;
      padding: 0 15px;
      height: 50px;
      background: linear-gradient(90deg, #fceceb 0%, #fff 100%);
      border-bottom: 1px solid #f8f8f8;
      
      .tab-item {
        margin-right: 20px;
        font-size: 14px;
        color: #666;
        cursor: pointer;
        position: relative;
        height: 100%;
        display: flex;
        align-items: center;
        
        &.active {
          color: #e1251b;
          font-weight: bold;
          &::after {
            content: '';
            position: absolute;
            bottom: 5px;
            left: 50%;
            transform: translateX(-50%);
            width: 20px;
            height: 3px;
            background: #e1251b;
            border-radius: 2px;
          }
        }
      }
      
      .close-btn {
        margin-left: auto;
        font-size: 18px;
        color: #ccc;
        cursor: pointer;
        &:hover { color: #e1251b; }
      }
    }

    .panel-body {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-thumb { background: #eee; border-radius: 2px; }

      .panel-coupon-card {
        background: #fff;
        border: 1px solid #fceceb;
        border-radius: 8px;
        margin-bottom: 12px;
        padding: 12px;
        position: relative;
        overflow: hidden;
        
        &::after {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: #e1251b;
        }

        .card-main {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
          
          .amt-box {
            width: 70px;
            color: #e1251b;
            .u { font-size: 12px; }
            .v { font-size: 24px; font-weight: 800; font-family: 'Arial'; }
          }
          
          .info-box {
            flex: 1;
            .name { font-size: 13px; font-weight: bold; color: #333; margin-bottom: 2px; }
            .cond { font-size: 11px; color: #999; }
          }
        }

        &.my-card {
           &.status-1, &.status-2 {
              background: #fdfdfd;
              .amt-box, .info-box .name, .info-box .cond { opacity: 0.5; }
              &::after { background: #ccc; }
           }
        }

        .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-top: 1px dashed #f0f0f0;
          padding-top: 10px;
          
          .valid { font-size: 10px; color: #999; }
          .btn, .status-btn {
            background: #e1251b;
            color: #fff;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 11px;
            cursor: pointer;
            transition: all 0.2s;
            &:hover { background: #c91623; transform: translateY(-1px); }
          }
          .status-text {
            font-size: 11px;
            color: #999;
          }
        }

        .edge-dot {
          position: absolute;
          left: -6px;
          width: 12px;
          height: 12px;
          background: #fafafa;
          border-radius: 50%;
          border: 1px solid #fceceb;
          &.doc-top { top: -6px; }
          &.doc-bottom { bottom: -6px; }
        }
      }

      .panel-empty {
        padding: 50px 0;
        text-align: center;
        color: #ccc;
        i { font-size: 40px; margin-bottom: 10px; display: block; opacity: 0.3; }
        p { font-size: 13px; margin: 0; }
      }
    }

    .panel-footer {
      padding: 10px;
      text-align: center;
      font-size: 11px;
      color: #ccc;
      background: #fdfdfd;
    }
  }

  // 展开状态
  &.expanded {
    .coupon-panel {
      transform: translateX(0) scale(1);
      opacity: 1;
      visibility: visible;
    }
  }
}

@keyframes wobble {
  0%, 100% { transform: rotate(0); }
  25% { transform: rotate(-15deg); }
  75% { transform: rotate(15deg); }
}

// 移除之前的优惠券样式
.available-coupons { display: none; }

.selected-categories {
  margin: 40px 0;

  .module-title {
    font-size: 22px;
    color: #1a2a40;
    margin-bottom: 25px;
    font-weight: bold;
    display: flex;
    align-items: center;
    &::before {
      content: '';
      width: 4px;
      height: 20px;
      background: #d97706;
      margin-right: 12px;
      border-radius: 2px;
    }
  }

  .category-cards {
    display: flex;
    justify-content: space-between;
    
    .cat-card {
      width: calc(20% - 12px);
      min-height: 140px;
      background: #fff;
      border-radius: 8px;
      display: flex;
      padding: 16px;
      box-sizing: border-box;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border: 1px solid #f0f0f0;
      position: relative;
      overflow: hidden;

      .card-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        z-index: 2;
        min-width: 0;

        .card-title {
          font-size: 16px;
          font-weight: bold;
          color: #333;
          margin: 0 0 8px 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .sub-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-bottom: 8px;

          span {
            font-size: 11px;
            color: #999;
            background: #f5f5f5;
            padding: 2px 6px;
            border-radius: 2px;
            word-break: break-word;
            white-space: normal;
          }
        }

        .go-detail {
          font-size: 12px;
          color: #d97706;
          font-weight: bold;
          display: flex;
          align-items: center;
          gap: 4px;
          opacity: 0.8;
          transition: opacity 0.2s;
        }
      }

      .card-image {
        width: 80px;
        height: 80px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 10px;

        img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          transition: transform 0.4s ease;
        }
      }

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 10px 25px rgba(217, 119, 6, 0.12);
        border-color: #d97706;
 
        .card-image img {
          transform: scale(1.1) rotate(5deg);
        }
 
        .go-detail {
          opacity: 1;
        }
      }
    }
  }
}

// 精选品类卡片响应式
@media (max-width: 1399px) and (min-width: 1200px) {
  .selected-categories .category-cards {
    gap: 10px;
    .cat-card { width: calc(20% - 10px); min-width: 220px; }
  }
}

@media (max-width: 1199px) and (min-width: 992px) {
  .selected-categories .category-cards {
    flex-wrap: wrap;
    gap: 12px;
    .cat-card { 
      width: calc(25% - 12px); 
      min-width: 220px;
      min-height: 160px;
    }
  }
}

@media (max-width: 991px) and (min-width: 768px) {
  .selected-categories .category-cards {
    flex-wrap: wrap;
    gap: 10px;
    .cat-card { 
      width: calc(33.33% - 10px); 
      min-width: 230px;
      min-height: 150px;
    }
  }
}

.finder-section {
  margin: 40px auto;
}

// 动态楼层样式
.dynamic-floor {
  margin-bottom: 40px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);

  .floor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 20px;
    background: #fff;
    border-bottom: 1px solid #eee;

    h3 {
      font-size: 18px;
      color: #333;
      font-weight: bold;
      margin: 0;
      display: flex;
      align-items: center;
      
      &::before {
        display: none;
      }
    }

    .more-link a {
      color: #999;
      font-size: 13px;
      transition: color 0.2s;
      &:hover { color: #d97706; }
    }
  }

  .floor-content {
    display: flex;
    padding: 15px;
    background: #fff;

    .floor-left-banner {
      width: 220px;
      height: 480px;
      padding: 24px 16px;
      flex-shrink: 0;
      border-radius: 8px;
      background-color: #1a2a40;
      background-size: cover;
      background-position: center bottom;
      background-repeat: no-repeat;
      display: flex;
      flex-direction: column;
      position: relative;
      overflow: hidden;
      box-shadow: inset 0 0 30px rgba(0,0,0,0.5);

      .floor-tag-wrapper {
        margin-bottom: 20px;
        position: relative;
        z-index: 2;
        
        .floor-number {
          font-size: 32px;
          font-weight: 900;
          color: rgba(255,255,255,0.15);
          line-height: 1;
          font-family: 'Arial Black', sans-serif;
        }
        
        .floor-divider {
          width: 30px;
          height: 3px;
          background: #d97706;
          margin-top: 8px;
          border-radius: 2px;
        }
      }

      .sub-category-tags {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        position: relative;
        z-index: 2;

        span {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 38px;
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(4px);
          color: rgba(255,255,255,0.9);
          font-size: 13px;
          font-weight: 500;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(255, 255, 255, 0.1);
          text-align: center;
          padding: 0 4px;
          box-sizing: border-box;

          &:hover {
            background: rgba(255, 255, 255, 0.15);
            border-color: rgba(255, 255, 255, 0.3);
            transform: translateY(-1px);
          }

          &.active {
            background: #fff;
            color: #1a2a40;
            border-color: #fff;
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
            font-weight: bold;
          }
        }
      }
    }

    .floor-products {
      flex: 1;
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      grid-template-rows: repeat(2, 1fr);
      gap: 15px;
      padding-left: 20px;
      align-content: stretch;

      .product-card {
        width: 100%;
        margin: 0;
        padding: 5px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;
        background: #fff;
        display: flex;
        flex-direction: column;

        &:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }

        .p-img {
          width: 180rpx;
          height: 180rpx;
          height: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;

          img {
            max-width: 90%;
            max-height: 90%;
            object-fit: contain;
          }
        }

        .p-info {
          .p-name {
            font-size: 12px;
            color: #333;
            line-height: 18px;
            height: 36px;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            margin-bottom: 5px;
          }

          .p-model {
            font-size: 11px;
            color: #999;
            margin-bottom: 5px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .p-price-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            
            .p-price {
              font-size: 17px;
              color: #d97706;
              font-weight: bold;
            }
            
            .cart-btn {
              width: 30px;
              height: 30px;
              background: #d97706;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: all 0.2s;
              
              i {
                color: #fff;
                font-size: 14px;
              }
              
              &:hover {
                background: #b45309;
                transform: scale(1.1) rotate(10deg);
              }
            }
          }
        }
      }

      .empty-products {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 0;
        color: #999;

        i { font-size: 48px; margin-bottom: 15px; }
      }
    }
  }
}

// 动态楼层响应式
@media (max-width: 1399px) and (min-width: 1200px) {
  .dynamic-floor .floor-content .floor-products .product-card {
    width: calc(20% - 10px); // 5列
  }
}

@media (max-width: 1199px) and (min-width: 992px) {
  .dynamic-floor .floor-content {
    .floor-left-banner { width: 200px; padding: 15px 10px; }
    .floor-products .product-card { width: calc(25% - 10px); } // 4列
  }
}

@media (max-width: 991px) and (min-width: 768px) {
  .dynamic-floor .floor-content {
    flex-wrap: wrap;
    .floor-left-banner { 
      width: 100%; 
      padding: 15px;
      .sub-category-tags { justify-content: center; }
    }
    .floor-products { 
      width: 100%;
      .product-card { width: calc(33.33% - 10px); } // 3列
    }
  }
}

// SKU 选择弹窗样式
.sku-dialog-content {
  .product-brief {
    display: flex;
    padding-bottom: 20px;
    border-bottom: 1px solid #f5f5f5;
    margin-bottom: 20px;
    
    img {
      width: 100px;
      height: 100px;
      object-fit: contain;
      border: 1px solid #eee;
      margin-right: 15px;
    }
    
    .brief-info {
      .name {
        font-size: 14px;
        color: #333;
        margin-bottom: 10px;
        line-height: 20px;
      }
      .price {
        color: #d97706;
        .currency { font-size: 14px; }
        .value { font-size: 22px; font-weight: bold; }
      }
      .selected-text {
        font-size: 12px;
        color: #666;
        margin-top: 8px;
      }
      .moq-tip {
        font-size: 12px;
        color: #e1251b;
        margin-top: 4px;
      }
    }
  }
  
  .spec-list-wrap {
    .spec-section {
      margin-bottom: 15px;
      
      .spec-title {
        font-size: 13px;
        color: #666;
        margin-bottom: 10px;
      }
      
      .sku-options {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        
        .sku-option-item {
          padding: 6px 15px;
          border: 1px solid #ddd;
          border-radius: 2px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
          
          &:hover {
            border-color: #d97706;
            color: #d97706;
          }
          
          &.active {
            background-color: #fffbeb;
            border-color: #d97706;
            color: #d97706;
            font-weight: bold;
          }
        }
      }
    }
  }
  
  .quantity-row {
    display: flex;
    align-items: center;
    padding-top: 15px;
    border-top: 1px solid #f5f5f5;
    
    .label {
      font-size: 13px;
      color: #666;
      margin-right: 15px;
    }
  }
}
</style>
